import { useGetContractDeviationTypes, useGetContractDeviationUnits } from "@/services/ContractDeviations";
import { Contract, ContractDeviation, ContractDeviationType } from "@/types";
import { Grid } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditContractInput } from "../../validation/editContract";
import { UseFormReturn } from "react-hook-form";
import EditableCheckboxList from "@/components/atoms/EditableCheckboxList";
import Text from "@/components/atoms/Text";
import Loader from "@/components/atoms/Loader";

interface DeviationProps {
  contract: Contract;
  editContractForm: UseFormReturn<EditContractInput>;
  disableForm: boolean;
}

const Deviations = ({ contract, editContractForm, disableForm }: DeviationProps) => {
  const { t } = useTranslation();

  const [contractDeviationOptions, setContractDeviationOptions] = useState<ContractDeviationType[]>([]);

  const { data: deviationUnits } = useGetContractDeviationUnits();
  const { data: contractDeviationData, isLoading: isLoadingDeviationsData } = useGetContractDeviationTypes(
    contract.createdByCompany,
    false,
    {}
  );

  useEffect(() => {
    if (contractDeviationData) {
      const mappedDeviations = contractDeviationData.map((deviationType) => {
        const existingDeviation = contract.contractDeviations.find(
          (cd) => cd.contractDeviationTypeId === deviationType.id
        );

        if (existingDeviation) {
          return { ...deviationType, price: existingDeviation.price };
        }
        return deviationType;
      });

      setContractDeviationOptions(mappedDeviations);
    }
  }, [contractDeviationData, contract]);

  if (isLoadingDeviationsData) {
    return <Loader />;
  }

  return (
    <Grid>
      {contractDeviationOptions.length > 0 && deviationUnits ? (
        <Grid.Col span={6}>
          <EditableCheckboxList
            disabled={disableForm}
            items={contractDeviationOptions}
            editableField="price"
            gridSpan={{ base: 4 }}
            getCheckboxLabel={(deviation) => deviation.name}
            isChecked={(deviation) =>
              editContractForm
                .watch("contractDeviations")
                .some((cd: ContractDeviation) => cd.contractDeviationTypeId === deviation.id)
            }
            onCheckboxToggle={(selectedDeviation, isChecked) => {
              const deviationUnit = deviationUnits.find((du) => du.id === selectedDeviation.id);
              const newDeviation: ContractDeviation = {
                contractDeviationTypeId: selectedDeviation.id,
                price: selectedDeviation.price,
                priceUnit: deviationUnit!,
                currency: selectedDeviation.currency,
              };
              const selectedDeviations = editContractForm.getValues("contractDeviations");
              editContractForm.setValue(
                "contractDeviations",
                isChecked
                  ? [...selectedDeviations, newDeviation]
                  : selectedDeviations.filter(
                      (s: ContractDeviation) => s.contractDeviationTypeId !== selectedDeviation.id
                    )
              );
            }}
            getTextInputValue={(s) => s.price}
            getTextInputSuffix={(s) => {
              const priceUnit = deviationUnits.find((du) => du.id === s.priceUnit)
                ? t("enumerables:priceUnit." + deviationUnits.find((du) => du.id === s.priceUnit)?.code)
                : "";
              return `${s.currency}/${priceUnit}`;
            }}
            onTextInputChange={(item, newValue) => {
              const updatedOptions = contractDeviationOptions.map((cd) => {
                if (cd.id === item.id) {
                  const parsed = parseFloat(newValue);
                  return { ...cd, price: isNaN(parsed) ? 0 : parsed };
                }
                return cd;
              });
              setContractDeviationOptions(updatedOptions);

              const updatedDeviations = editContractForm.getValues("contractDeviations").map((cd) => {
                if (cd.contractDeviationTypeId === item.id) {
                  const parsed = parseFloat(newValue);
                  return { ...cd, price: isNaN(parsed) ? 0 : parsed };
                }
                return cd;
              });

              editContractForm.setValue("contractDeviations", updatedDeviations);
            }}
          />
        </Grid.Col>
      ) : (
        <Grid.Col span={6}>
          <Text>{t("contracts:contractDeviation.noDeviationsAdded")}</Text>
        </Grid.Col>
      )}

      <Grid.Col span={6}>
        <Text color="secondary">{t("contracts:contractDeviation.deviationStepInfo")}</Text>
      </Grid.Col>
    </Grid>
  );
};

export default Deviations;
